import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "@/utils/axiosInstance";
import { handleThunkError } from "@/utils/thunkErrorHandler";
import { Params } from "@/types";
import { InferType } from "yup";
import { clientSchema } from "@/validations";

type ClientFormData = InferType<typeof clientSchema>;

const endpoint = {
  clients: "/clients",
};

export const fetchClients = createAsyncThunk(
  "clients/fetchClients",
  async (params: Params, thunkAPI) => {
    try {
      const response = await axiosInstance.get(endpoint.clients, { params });
      return response.data;
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao carregar clientes");
    }
  },
);

export const fetchClientsAll = createAsyncThunk(
  "clients/fetchClientsAll",
  async (
    params: Omit<Params, "current_page" | "per_page"> & {
      without_pagination: 1;
    },
    thunkAPI,
  ) => {
    try {
      // Fazer requisição direta sem cache - apenas manter na sessão
      const response = await axiosInstance.get(endpoint.clients, {
        params,
      });
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Erro ao carregar todos os clientes",
      );
    }
  },
);

export const createClient = createAsyncThunk(
  "clients/createClient",
  async (data: any, thunkAPI) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && data instanceof FormData;
      const response = await axiosInstance.post(endpoint.clients, data, {
        headers: isFormData
          ? { "Content-Type": "multipart/form-data" }
          : undefined,
      });
      return response.data;
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao cadastrar cliente");
    }
  },
);

export const updateClient = createAsyncThunk(
  "clients/updateClient",
  async ({ slug, client }: { slug: string; client: any }, thunkAPI) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && client instanceof FormData;
      // Se multipart, usar POST + _method override para evitar problemas com some servers
      const methodUrl = isFormData
        ? `${endpoint.clients}/${slug}?_method=PUT`
        : `${endpoint.clients}/${slug}`;
      const httpMethod = isFormData ? "post" : "put";
      // @ts-ignore
      const response = await axiosInstance[httpMethod](methodUrl, client, {
        headers: isFormData
          ? { "Content-Type": "multipart/form-data" }
          : undefined,
      });
      return response.data;
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao atualizar cliente");
    }
  },
);

export const deleteClient = createAsyncThunk(
  "clients/deleteClient",
  async (slug: string, thunkAPI) => {
    try {
      const response = await axiosInstance.delete(
        `${endpoint.clients}/${slug}`,
      );
      return response.data;
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao excluir cliente");
    }
  },
);

export const searchClients = createAsyncThunk(
  "clients/searchClients",
  async (
    { searchTerm, per_page = 15 }: { searchTerm: string; per_page?: number },
    thunkAPI,
  ) => {
    try {
      const params: any = {
        per_page,
      };
      if (searchTerm && searchTerm.trim() !== "" && searchTerm.trim() !== "*") {
        params.search = searchTerm;
      }

      const response = await axiosInstance.get(endpoint.clients, {
        params,
      });
      return response.data;
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao buscar clientes");
    }
  },
);
